import { useEffect, useState } from "react";
import { useAuth } from "@clerk/react";
import { AlertTriangle, Loader2, Users, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";

const PER_SEAT_PLANS = new Set(["track", "draft"]);

type Level = "warning" | "at_limit";

function dismissKey(orgId: number | string, level: Level): string {
  return `zindeal.seatUsageBanner.dismissed.${orgId}.${level}`;
}

export function SeatUsageBanner({
  orgId,
  plan,
  purchasedSeats,
  coachCount,
}: {
  orgId: number | string;
  plan: string;
  purchasedSeats: number;
  coachCount: number;
}) {
  const { getToken } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const seats = Math.max(1, purchasedSeats || 1);
  const warningThreshold = Math.ceil(seats * 0.8);
  const atLimit = coachCount >= seats;
  const atWarning = coachCount >= warningThreshold;
  const level: Level | null = atLimit ? "at_limit" : atWarning ? "warning" : null;

  useEffect(() => {
    if (!level) {
      setDismissed(false);
      return;
    }
    try {
      setDismissed(sessionStorage.getItem(dismissKey(orgId, level)) === "1");
    } catch {
      setDismissed(false);
    }
  }, [orgId, level]);

  if (!PER_SEAT_PLANS.has(plan)) return null;
  if (!level) return null;
  if (dismissed) return null;

  const usagePct = Math.min(100, Math.round((coachCount / seats) * 100));

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(dismissKey(orgId, level), "1");
    } catch {
      /* sessionStorage may be unavailable; just hide locally */
    }
    setDismissed(true);
  };

  async function openBillingPortal() {
    setLoading(true);
    try {
      const token = await getToken();
      const res = await fetch("/api/billing/portal", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const data = (await res.json()) as { url?: string; error?: string };
      if (!res.ok || !data.url) {
        toast({
          title: "Could not open billing",
          description: data.error ?? "Please try again.",
          variant: "destructive",
        });
        return;
      }
      window.location.href = data.url;
    } catch {
      toast({
        title: "Network error",
        description: "Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  const isAtLimit = level === "at_limit";

  const wrapperClass = isAtLimit
    ? "border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-900"
    : "border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900";
  const iconWrapClass = isAtLimit
    ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
    : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300";
  const headingClass = isAtLimit
    ? "text-red-900 dark:text-red-100"
    : "text-amber-900 dark:text-amber-100";
  const bodyClass = isAtLimit
    ? "text-red-800/90 dark:text-red-200/90"
    : "text-amber-800/90 dark:text-amber-200/90";
  const progressIndicator = isAtLimit ? "bg-red-500" : "bg-amber-500";

  const Icon = isAtLimit ? AlertTriangle : Users;

  const heading = isAtLimit
    ? "You've reached your purchased seat limit"
    : "You're approaching your seat limit";
  const body = isAtLimit
    ? `You've used ${coachCount} of ${seats} purchased seats. Add more seats to keep adding coaches.`
    : `You've used ${coachCount} of ${seats} purchased seats (${usagePct}%). Consider adding seats before you hit the limit.`;
  const ctaLabel = isAtLimit ? "Add seats" : "Manage seats";

  return (
    <div
      data-testid="seat-usage-banner"
      data-level={level}
      className={`relative rounded-xl border p-4 ${wrapperClass}`}
    >
      <div className="flex items-start gap-3 pr-8">
        <span
          className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${iconWrapClass}`}
        >
          <Icon className="h-5 w-5" />
        </span>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${headingClass}`}>{heading}</p>
          <p className={`text-xs mt-0.5 ${bodyClass}`}>{body}</p>
          <Progress
            value={usagePct}
            className="mt-3 h-1.5"
            indicatorClassName={progressIndicator}
          />
        </div>
        <div className="flex-shrink-0">
          <Button
            size="sm"
            onClick={openBillingPortal}
            disabled={loading}
            data-testid="seat-usage-banner-cta"
            variant={isAtLimit ? "default" : "outline"}
          >
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {ctaLabel}
          </Button>
        </div>
      </div>
      <button
        type="button"
        aria-label="Dismiss seat usage warning"
        onClick={handleDismiss}
        data-testid="seat-usage-banner-dismiss"
        className="absolute top-2 right-2 inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-black/5 dark:hover:bg-white/10"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
